import gql from 'graphql-tag';
import { clinicFragments } from './Clinics';
import { addressFragments } from './Address';
import { providerFragments } from './Providers';
import { recurrencePatternFragments } from './RecurrencePattern';
import { appointmentTypesFragments } from './AppointmentTypes';

export const simpleEvent = gql`
  fragment Event on Event {
    id
    athenaId
    athenaStatus
    crId
    authorizedTherapyId
    crExecutionId
    crStatus
    clinicPreference
    sfrReasonId
    instanceIndex
    isPendingConfirmation
    startDate
    endDate
    duration
    locationType
    locationCategory
    paymentMethod
    status
    subject
    telehealthLink
  }
`;

export const eventsFragments = {
  providerEvent: gql`
    fragment ProviderEvent on Event {
      ...Event
      masterId
      appointmentType {
        ...AppointmentTypeWithParent
      }
      client {
        id
        name
        profile {
          dob
        }
      }
      clinic {
        id
        name
        abbreviation
        timezone
      }
      recurrencePattern {
        recurringUntil
        recurringEvery
      }
    }
    ${appointmentTypesFragments.appointmentTypeWithParent}
    ${simpleEvent}
  `,
  EventInput: gql`
    fragment EventInput on Event {
      id
      clinicPreference
      sfrReasonId
      isPendingConfirmation
      startDate
      endDate
      duration
      locationType
      locationCategory
      paymentMethod
      telehealthLink
      note {
        id
        note
      }
      address {
        ...AddressData
      }
      recurrencePattern {
        ...RecurrencePatternData
      }
      appointmentType {
        id
      }
      client {
        id
      }
      provider {
        id
      }
      clinic {
        id
        timezone
      }
      room {
        id
      }
    }
    ${addressFragments.addressData}
    ${recurrencePatternFragments.recurrencePattern}
  `,
  eventData: gql`
    fragment EventData on Event {
      id
      master {
        id
      }
      subject
      startDate
      endDate
      duration
      status
      locationType
      locationCategory
      clinic {
        id
        name
        abbreviation
      }
      appointmentType {
        ...AppointmentTypeWithParent
      }
      room {
        id
        name
        email
      }
      provider {
        id
        firstName
        lastName
        displayName
      }
      recurrencePattern {
        ...RecurrencePatternData
      }
      clinicPreference
      sfrReasonId
      paymentMethod
      locationCategory
      locationType
      type
    }
    ${appointmentTypesFragments.appointmentTypeWithParent}
    ${recurrencePatternFragments.recurrencePattern}
  `,
  calendarEventData: gql`
    fragment CalendarEventData on Event {
      id
      subject
      startDate
      endDate
      status
      locationType
      isDelete
      master {
        id
      }
      address {
        ...AddressData
      }
      locationCategory
      recurrencePattern {
        id
        recurringEvery
      }
      crStatus
      athenaStatus
      isPendingConfirmation
      bloomNoteId

      appointmentType {
        ...AppointmentTypeWithParent
      }
      client {
        id
        name
        firstName
        lastName
        clinic {
          id
          abbreviation
          timezone
        }
        __typename @skip(if: true)
      }
      clinic {
        id
        name
        abbreviation
        timezone
        __typename
      }
      provider {
        id
        name
        lastName
        firstName
        displayName
        speciality {
          id
          abbreviation
        }
        clinic {
          id
          abbreviation
          timezone
        }
        __typename
      }
      room {
        id
        name
        __typename
      }
      paymentMethod
      type
      isObservation
      leadEvent {
        id
        provider {
          id
          name
          lastName
          firstName
          displayName
        }
      }
      observationsCount
      observations {
        id
        provider {
          ...Provider
        }
      }
      __typename @skip(if: true)
    }
    ${providerFragments.provider}
    ${addressFragments.addressData}
    ${appointmentTypesFragments.appointmentTypeWithParent}
  `,
  calendarPlannerEventData: gql`
    fragment calendarPlannerEventData on Event {
      id
      subject
      startDate
      endDate
      duration
      status
      locationType
      telehealthLink
      clinicPreference
      sfrReasonId
      isDelete
      master {
        id
      }
      address {
        ...AddressData
      }
      locationCategory
      recurrencePattern {
        id
        recurringEvery
        recurringUntil
      }
      crStatus
      athenaStatus
      isPendingConfirmation
      isConverted
      bloomNoteId

      appointmentType {
        ...AppointmentTypeWithParent
      }
      client {
        id
        name
        firstName
        lastName
        clinic {
          id
          abbreviation
          timezone
        }
        profile {
          dob
        }
        __typename @skip(if: true)
      }
      clinic {
        id
        name
        abbreviation
        timezone
        __typename
      }
      provider {
        id
        name
        lastName
        firstName
        displayName
        speciality {
          id
          abbreviation
          department
        }
        clinic {
          id
          abbreviation
          timezone
        }
        __typename
      }
      note {
        note
      }
      room {
        id
        name
      }
      paymentMethod
      type
      isObservation
      leadEvent {
        id
        provider {
          id
          name
          lastName
          firstName
          displayName
        }
      }
      observationsCount
      observations {
        id
        provider {
          ...Provider
        }
      }
      __typename @skip(if: true)
    }
    ${providerFragments.provider}
    ${addressFragments.addressData}
    ${appointmentTypesFragments.appointmentTypeWithParent}
  `,
  fullEventData: gql`
    fragment FullEventData on Event {
      id
      subject
      startDate
      duration
      endDate
      originalStartDate
      originalEndDate
      status
      type
      note {
        id
        note
      }
      master {
        id
      }
      crStatus
      athenaStatus
      isPendingConfirmation
      bloomNoteId
      locationCategory
      locationType
      clinicPreference
      sfrReasonId
      telehealthLink
      clinic {
        id
        name
        abbreviation
        timezone
      }
      client {
        id
        clinic {
          id
          name
          abbreviation
          timezone
        }
        name
        lastName
        firstName
        firstNameUsed
        profile {
          id
          dob
          legalSex
          gender
          phenoType
          phonePreference
          preferredPhone
        }
      }
      provider {
        ...Provider
      }
      appointmentType {
        id
        title
        headerColor
        backgroundColor
        eventType {
          id
          name
        }
        parent {
          id
          title
          headerColor
          backgroundColor
          eventType {
            id
            name
          }
        }
      }
      recurrencePattern {
        ...RecurrencePatternData
      }
      eventAudit {
        id
        action
        actionBy
        actionAt
        reason
        note
        directEditChanges
      }

      address {
        ...AddressData
      }
      room {
        ...RoomsData
      }
      paymentMethod
      syncAction {
        id
        retryCount
        failureReason
      }
      observations {
        id
        provider {
          ...Provider
        }
        clinic {
          id
          timezone
        }
        startDate
        endDate
        locationType
        telehealthLink
        duration
      }
      isObservation
      observationsCount
      driveTimeSourceAddr
      driveTimeDesteAddr
      driveTimeMileage
      isFirstOrLastDriveToNonClinic
      reimbursableMileage
    }
    ${providerFragments.provider}
    ${addressFragments.addressData}
    ${recurrencePatternFragments.recurrencePattern}
    ${clinicFragments.roomsData}
  `,
  fullObserverEventData: gql`
    fragment FullObserverEventData on Event {
      id
      subject
      startDate
      duration
      endDate
      status
      telehealthLink
      locationType
      locationCategory
      address {
        ...AddressData
      }
      clinic {
        id
        timezone
      }
      appointmentType {
        id
        title
        headerColor
        backgroundColor
        eventType {
          id
          name
        }
        parent {
          id
          title
          headerColor
          backgroundColor
          eventType {
            id
            name
          }
        }
      }
      provider {
        ...Provider
      }
      eventAudit {
        id
        action
        actionBy
        actionAt
        reason
        note
        directEditChanges
      }
      leadEvent {
        id
        locationCategory
        locationType
        telehealthLink
        provider {
          ...Provider
        }
        clinic {
          id
          name
          abbreviation
          timezone
        }
        client {
          id
          clinic {
            id
            name
            abbreviation
            timezone
          }
          name
          lastName
          firstName
          firstNameUsed
        }
        address {
          ...AddressData
        }
        room {
          ...RoomsData
        }
      }
    }
    ${providerFragments.provider}
    ${addressFragments.addressData}
    ${clinicFragments.roomsData}
  `,
  calendarCancelledEventData: gql`
    fragment CalendarCancelledEventData on CanceledEvent {
      id
      originalEventId
      subject
      startDate
      endDate
      status
      locationType
      locationCategory
      recurringType
      recurringEvery
      recurringUntil
      crStatus
      athenaStatus
      isPendingConfirmation
      appointmentType {
        ...AppointmentTypeWithParent
      }
      client {
        id
        name
        firstName
        lastName
        name
        __typename
      }
      clinic {
        id
        name
        abbreviation
        timezone
        __typename
      }
      provider {
        id
        name
        lastName
        firstName
        displayName
        __typename
      }
      room {
        id
        name
        __typename
      }
      paymentMethod
      __typename
    }
    ${appointmentTypesFragments.appointmentTypeWithParent}
  `
};
